import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_CLIENT_TYPES } from '@lims/shared-types';
import { createClientSchema, updateClientSchema } from '@lims/shared-validation';
import { ApiError } from '../../api/client.js';
import {
  clientsApi,
  settingsApi,
  type ClientDetail,
  type DuplicateMatch,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  Alert,
  CheckboxField,
  ErrorState,
  FormPage,
  FormSection,
  LoadingState,
  SelectField,
  TextAreaField,
  TextField,
  useDebouncedValue,
  useSaveErrors,
  useSaveMutation,
  validationErrors,
  useLoadOnce,
} from '../../components/ui/index.js';
import { DuplicateMatchList } from './DuplicateMatchList.js';
import { optionalText } from './bilingual.js';

/**
 * Create or edit a client — one compact form (UX pass, D188).
 *
 * THREE SHORT SECTIONS. Identity (name, type, commercial registration, tax
 * number), contact (one email, ONE telephone written however the desk writes
 * it) and address (address, city). The registered legal name and the country
 * code left the form: neither was needed to receive work, and both made the
 * first screen look like paperwork. A laboratory that REQUIRES a field (its
 * own intake policy, in settings) sees it marked, and the server enforces it.
 *
 * DUPLICATES WARN, THEY DO NOT BLOCK. There is no separate search step any
 * more. While the desk types, the form asks the server whether a similar
 * client exists and shows who, and why, with a link that opens the existing
 * file in a new tab. A match strong enough to matter must be answered by name
 * — "this is a different client" — before the save succeeds; the server
 * re-runs detection on the submitted data regardless, and refuses (409) any
 * strong match nobody answered. Nothing is ever merged: a new client is
 * always a new record.
 */

interface ClientValues {
  name: string;
  clientType: string | null;
  commercialRegistrationNumber: string;
  vatRegistrationNumber: string;
  contactEmail: string;
  contactPhone: string;
  addressLine: string;
  city: string;
  notes: string;
  reason: string;
}

const EMPTY: ClientValues = {
  name: '',
  clientType: 'company',
  commercialRegistrationNumber: '',
  vatRegistrationNumber: '',
  contactEmail: '',
  contactPhone: '',
  addressLine: '',
  city: '',
  notes: '',
  reason: '',
};

function valuesFrom(client: ClientDetail): ClientValues {
  return {
    name: client.name,
    clientType: client.clientType,
    commercialRegistrationNumber: client.commercialRegistrationNumber ?? '',
    vatRegistrationNumber: client.vatRegistrationNumber ?? '',
    contactEmail: client.contactEmail ?? '',
    contactPhone: client.contactPhone ?? '',
    addressLine: client.addressLine ?? '',
    city: client.city ?? '',
    notes: client.notes ?? '',
    reason: '',
  };
}

/** Enough typed to be worth asking about: a name of three characters, or any
 *  identifier at all. */
function probeFrom(values: ClientValues) {
  const probe: Record<string, string> = {};
  if (values.name.trim().length >= 3) probe.name = values.name.trim();
  if (values.contactEmail.trim()) probe.contactEmail = values.contactEmail.trim();
  if (values.contactPhone.trim()) probe.contactPhone = values.contactPhone.trim();
  if (values.vatRegistrationNumber.trim()) {
    probe.vatRegistrationNumber = values.vatRegistrationNumber.trim();
  }
  if (values.commercialRegistrationNumber.trim()) {
    probe.commercialRegistrationNumber = values.commercialRegistrationNumber.trim();
  }
  return probe;
}

export function ClientFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();

  const isNew = id === undefined;
  const canSave = can(isNew ? PERMISSIONS.CLIENTS_CREATE : PERMISSIONS.CLIENTS_UPDATE);

  const [values, setValues] = useState<ClientValues>(EMPTY);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [acknowledgementNote, setAcknowledgementNote] = useState('');

  const set = <Field extends keyof ClientValues>(field: Field) =>
    (value: ClientValues[Field]) => {
      setValues((current) => ({ ...current, [field]: value }));
      setLocalErrors((current) => {
        if (current[field] === undefined) return current;
        const { [field]: _removed, ...rest } = current;
        return rest;
      });
    };

  const client = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.get(id as string),
    enabled: !isNew,
  });

  // Left blank until the client's own values arrive: a form pre-filled with
  // defaults would briefly show something that is not this client's data.
  useLoadOnce(client.data, (record) => {
    setValues(valuesFrom(record));
  });

  /** This laboratory's own intake policy. Read, never assumed. */
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    retry: false,
    enabled: isNew,
  });
  const required = settings.data?.clientRequiredFields ?? [];
  const isRequired = (field: string) => isNew && required.includes(field);

  // Possible duplicates, asked for while the desk types.
  const probe = useDebouncedValue(
    useMemo(() => probeFrom(values), [values]),
    500,
  );
  const duplicates = useQuery({
    queryKey: ['client-duplicates', probe],
    queryFn: () => clientsApi.searchDuplicates(probe),
    enabled: isNew && canSave && Object.keys(probe).length > 0,
    retry: false,
    staleTime: 30_000,
  });
  const liveMatches: readonly DuplicateMatch[] = duplicates.data?.matches ?? [];

  const body = () => ({
    name: values.name.trim(),
    ...(values.clientType ? { clientType: values.clientType } : {}),
    commercialRegistrationNumber: optionalText(values.commercialRegistrationNumber),
    vatRegistrationNumber: optionalText(values.vatRegistrationNumber),
    contactEmail: optionalText(values.contactEmail),
    contactPhone: optionalText(values.contactPhone),
    addressLine: optionalText(values.addressLine),
    city: optionalText(values.city),
    notes: optionalText(values.notes),
  });

  const save = useSaveMutation<ClientDetail>({
    mutationFn: () =>
      isNew
        ? clientsApi.create({
            ...body(),
            ...(acknowledged.length > 0
              ? {
                  acknowledgedDuplicateIds: acknowledged,
                  duplicateAcknowledgementNote: optionalText(acknowledgementNote),
                }
              : {}),
          })
        : clientsApi.update(id as string, {
            ...body(),
            ...(values.reason.trim() ? { reason: values.reason.trim() } : {}),
          }),
    successMessage: isNew ? t('clients.created') : t('clients.updated'),
    invalidate: [['clients'], ['client', id], ['client-file', id]],
    navigateTo: (saved) => `/clients/${saved.id}`,
  });

  /**
   * The strong matches the server refused to pass unanswered, by id. A match
   * the live check never showed (someone created it a moment ago) is shown
   * with what is known — its id — rather than hidden.
   */
  const refusedIds =
    save.error instanceof ApiError && save.error.code === 'DUPLICATE_RECORD'
      ? save.error.details
          .map((detail) => detail.message)
          .filter((value): value is string => Boolean(value))
      : [];
  const refusedUnknown: DuplicateMatch[] = refusedIds
    .filter((candidate) => !liveMatches.some((match) => match.clientId === candidate))
    .map((candidate) => ({
      clientId: candidate,
      clientNumber: '',
      name: '',
      contactEmail: null,
      contactPhone: null,
      status: 'active',
      confidence: 'strong',
      requiresAcknowledgement: true,
      reasons: [],
    }));
  const shownMatches = [...liveMatches, ...refusedUnknown];

  const { fields: serverErrors } = useSaveErrors(save.error);
  const errorFor = (field: string) => localErrors[field] ?? serverErrors[field];

  const submit = () => {
    const found = validationErrors(
      isNew ? createClientSchema : updateClientSchema,
      body(),
      t,
    );
    setLocalErrors(found);
    if (Object.keys(found).length > 0) return;
    save.mutate();
  };

  if (!isNew && client.isLoading) return <LoadingState label={t('common.loading')} />;
  if (!isNew && client.error) {
    return <ErrorState error={client.error} onRetry={() => void client.refetch()} />;
  }

  const title = isNew ? t('clients.createTitle') : t('clients.editTitle');
  const fieldLabels = {
    name: t('clients.name'),
    clientType: t('clients.clientType'),
    commercialRegistrationNumber: t('clients.commercialRegistrationNumber'),
    vatRegistrationNumber: t('clients.vatRegistrationNumber'),
    contactEmail: t('clients.contactEmail'),
    contactPhone: t('clients.contactPhone'),
    addressLine: t('clients.address'),
    city: t('clients.city'),
    notes: t('clients.notes'),
    reason: t('common.reasonOptional'),
  };

  const requiredHint = (field: string) =>
    isRequired(field) ? { description: t('clients.requiredByLaboratory') } : {};

  return (
    <FormPage
      title={title}
      {...(!isNew && client.data ? { subtitle: client.data.clientNumber } : {})}
      breadcrumbs={[
        { label: t('nav.clients'), to: '/clients' },
        ...(!isNew && client.data
          ? [{ label: client.data.name, to: `/clients/${id}` }]
          : []),
        { label: title },
      ]}
      onSubmit={submit}
      onCancel={() => navigate(isNew ? '/clients' : `/clients/${id}`)}
      isSubmitting={save.isPending}
      canSubmit={canSave}
      error={save.error}
      fieldLabels={fieldLabels}
      notices={
        !isNew && client.data?.status === 'suspended' ? (
          <Alert tone="warning" title={t('clients.suspend.banner')} />
        ) : null
      }
    >
      <FormSection
        title={t('clients.identity')}
        {...(isNew ? { description: t('clients.onlyNameRequired') } : {})}
      >
        <TextField
          label={t('clients.name')}
          value={values.name}
          onChange={set('name')}
          textDirection="auto"
          isRequired
          isReadOnly={!canSave}
          className="sm:col-span-2"
          {...(errorFor('name') ? { error: errorFor('name') } : {})}
        />
        <SelectField
          label={t('clients.clientType')}
          value={values.clientType}
          onChange={set('clientType')}
          isRequired={isRequired('clientType')}
          isDisabled={!canSave}
          options={ALL_CLIENT_TYPES.map((type) => ({
            value: type,
            label: t(`clients.type.${type}`),
          }))}
          {...(errorFor('clientType') ? { error: errorFor('clientType') } : {})}
        />
        <TextField
          label={t('clients.commercialRegistrationNumber')}
          value={values.commercialRegistrationNumber}
          onChange={set('commercialRegistrationNumber')}
          textDirection="ltr"
          isRequired={isRequired('commercialRegistrationNumber')}
          isReadOnly={!canSave}
          {...requiredHint('commercialRegistrationNumber')}
          {...(errorFor('commercialRegistrationNumber')
            ? { error: errorFor('commercialRegistrationNumber') }
            : {})}
        />
        <TextField
          label={t('clients.vatRegistrationNumber')}
          value={values.vatRegistrationNumber}
          onChange={set('vatRegistrationNumber')}
          textDirection="ltr"
          isRequired={isRequired('vatRegistrationNumber')}
          isReadOnly={!canSave}
          {...requiredHint('vatRegistrationNumber')}
          {...(errorFor('vatRegistrationNumber')
            ? { error: errorFor('vatRegistrationNumber') }
            : {})}
        />
      </FormSection>

      {isNew && shownMatches.length > 0 ? (
        <FormSection
          title={t('clients.duplicate.possibleTitle')}
          description={t('clients.duplicate.possibleBody')}
          columns={1}
        >
          {refusedIds.length > 0 ? (
            <Alert
              tone="warning"
              title={t('clients.duplicate.blockedTitle')}
              description={t('clients.duplicate.blockedBody')}
            />
          ) : null}
          <DuplicateMatchList
            matches={shownMatches}
            truncated={duplicates.data?.candidatesTruncated ?? false}
            renderAction={(match) =>
              match.requiresAcknowledgement ? (
                <CheckboxField
                  label={t('clients.duplicate.acknowledge')}
                  value={acknowledged.includes(match.clientId)}
                  onChange={(checked) =>
                    setAcknowledged((current) =>
                      checked
                        ? [...current, match.clientId]
                        : current.filter((value) => value !== match.clientId),
                    )
                  }
                />
              ) : null
            }
          />
          {acknowledged.length > 0 ? (
            <TextField
              label={t('clients.duplicate.acknowledgeNote')}
              description={t('clients.duplicate.acknowledgeNoteHint')}
              value={acknowledgementNote}
              onChange={setAcknowledgementNote}
              textDirection="auto"
            />
          ) : null}
        </FormSection>
      ) : null}

      <FormSection title={t('clients.contactDetails')}>
        <TextField
          label={t('clients.contactEmail')}
          type="email"
          value={values.contactEmail}
          onChange={set('contactEmail')}
          textDirection="ltr"
          isRequired={isRequired('contactEmail')}
          isReadOnly={!canSave}
          {...requiredHint('contactEmail')}
          {...(errorFor('contactEmail') ? { error: errorFor('contactEmail') } : {})}
        />
        <TextField
          label={t('clients.contactPhone')}
          type="tel"
          value={values.contactPhone}
          onChange={set('contactPhone')}
          textDirection="ltr"
          isRequired={isRequired('contactPhone')}
          isReadOnly={!canSave}
          {...requiredHint('contactPhone')}
          {...(errorFor('contactPhone') ? { error: errorFor('contactPhone') } : {})}
        />
      </FormSection>

      <FormSection title={t('clients.address')}>
        <TextField
          label={t('clients.address')}
          value={values.addressLine}
          onChange={set('addressLine')}
          textDirection="auto"
          isRequired={isRequired('addressLine')}
          isReadOnly={!canSave}
          {...requiredHint('addressLine')}
          {...(errorFor('addressLine') ? { error: errorFor('addressLine') } : {})}
        />
        <TextField
          label={t('clients.city')}
          value={values.city}
          onChange={set('city')}
          textDirection="auto"
          isReadOnly={!canSave}
          {...(errorFor('city') ? { error: errorFor('city') } : {})}
        />
      </FormSection>

      <FormSection title={t('clients.notes')} columns={1}>
        <TextAreaField
          label={t('clients.notes')}
          value={values.notes}
          onChange={set('notes')}
          rows={2}
          textDirection="auto"
          isReadOnly={!canSave}
          {...(errorFor('notes') ? { error: errorFor('notes') } : {})}
        />
        {!isNew ? (
          <TextField
            label={t('common.reasonOptional')}
            value={values.reason}
            onChange={set('reason')}
            textDirection="auto"
            isReadOnly={!canSave}
            {...(errorFor('reason') ? { error: errorFor('reason') } : {})}
          />
        ) : null}
      </FormSection>
    </FormPage>
  );
}
